"use client";

import { useRef } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { CategoryIcon } from "@/components/ui/category-icon";

export interface CategorySubmenuItem {
  slug: string;
  name: string;
  emoji?: string | null;
  /** Optional URL param value (defaults to `name` so the comparador filter
   *  matches against `Product.category`). */
  filterValue?: string;
  /** Optional accent color hex, used as background tint. */
  color?: string | null;
}

/**
 * Visual-only fallback used when the DB has no active categories yet. It
 * keeps the strip visible during early days / dev so the page doesn't
 * look broken; once admin seeds the Category table the DB list takes
 * over. Kept exactly aligned with the original hardcoded set.
 */
const DEFAULT_FALLBACK: ReadonlyArray<CategorySubmenuItem> = [
  { slug: "supermercado", name: "Despensa", emoji: "🛒", filterValue: "Supermercado" },
  { slug: "lacteos", name: "Lácteos", emoji: "🥛", filterValue: "Lácteos" },
  { slug: "celulares", name: "Celulares", emoji: "📱", filterValue: "Celulares" },
  { slug: "electronica", name: "Electrónica", emoji: "📺", filterValue: "Electrónica" },
  { slug: "computacion", name: "Computación", emoji: "💻", filterValue: "Computación" },
  { slug: "belleza", name: "Belleza", emoji: "✨", filterValue: "Belleza" },
  { slug: "medicamentos", name: "Medicamentos", emoji: "💊", filterValue: "Medicamentos" },
  { slug: "farmacia", name: "Farmacia", emoji: "🩺", filterValue: "Farmacia" },
  { slug: "hogar", name: "Hogar", emoji: "🏠", filterValue: "Hogar" },
  { slug: "limpieza", name: "Limpieza", emoji: "🧴", filterValue: "Limpieza" },
  { slug: "bebidas", name: "Bebidas", emoji: "🍷", filterValue: "Bebidas" },
  { slug: "snacks", name: "Snacks", emoji: "🍪", filterValue: "Snacks" },
  { slug: "carnes", name: "Carnes", emoji: "🍗", filterValue: "Carnes" },
  { slug: "frutas", name: "Frutas", emoji: "🍎", filterValue: "Frutas" },
  { slug: "deportes", name: "Deportes", emoji: "🏋️", filterValue: "Deportes" },
  { slug: "juguetes", name: "Juguetes", emoji: "🎮", filterValue: "Juguetes" },
  { slug: "bebes", name: "Bebés", emoji: "🍼", filterValue: "Bebés" },
  { slug: "mascotas", name: "Mascotas", emoji: "🐶", filterValue: "Mascotas" },
  { slug: "ropa", name: "Ropa", emoji: "👕", filterValue: "Ropa" },
  { slug: "calzado", name: "Calzado", emoji: "👟", filterValue: "Calzado" },
];

interface Props {
  categories?: ReadonlyArray<CategorySubmenuItem>;
}

/**
 * Liquid-glass horizontal category strip shown at the top of /comparador.
 * Sticks under the navbar (top-[68px]). Categories come from the admin
 * taxonomy when available, otherwise falls back to the curated default
 * list so the surface never renders empty.
 */
export function CategorySubmenu({ categories }: Props) {
  const list = categories && categories.length > 0 ? categories : DEFAULT_FALLBACK;
  const trackRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "prev" | "next") => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: dir === "next" ? 320 : -320, behavior: "smooth" });
  };

  return (
    <div className="sticky top-[68px] z-30 mx-auto w-full px-3 sm:px-4 lg:px-8 xl:px-10 2xl:px-12">
      {/* Liquid Glass container */}
      <div className="relative overflow-hidden rounded-2xl border border-white/40 dark:border-white/10 shadow-[0_8px_32px_rgba(15,23,42,0.12)] backdrop-blur-2xl bg-linear-to-b from-white/70 to-white/40 dark:from-zinc-900/60 dark:to-zinc-900/30">
        {/* Glass highlights */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-2xl"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.25) 100%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -left-10 w-64 h-32 rounded-full bg-white/40 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-14 right-0 w-72 h-32 rounded-full bg-blue-200/30 blur-3xl"
        />

        <div className="relative px-2 sm:px-3 py-2.5">
          <button
            type="button"
            onClick={() => scroll("prev")}
            aria-label="Anterior"
            className="hidden sm:flex absolute left-1 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm border border-white/60 dark:border-white/10 shadow-sm items-center justify-center hover:bg-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div
            ref={trackRef}
            className="flex gap-1 sm:gap-2 overflow-x-auto scroll-smooth sm:px-9 scrollbar-hide"
            style={{ scrollbarWidth: "none" }}
          >
            {list.map((cat) => {
              const filterValue = cat.filterValue ?? cat.name;
              const tint = cat.color ? `${cat.color}26` : undefined; // 26 = ~15% alpha
              return (
                <Link
                  key={cat.slug}
                  href={`/comparador?categoria=${encodeURIComponent(filterValue)}`}
                  className="group shrink-0 flex flex-col items-center gap-1 w-[72px] sm:w-20 py-1.5 px-1 rounded-xl hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center text-foreground/80 group-hover:text-primary transition-colors rounded-full text-xl"
                    style={tint ? { backgroundColor: tint } : undefined}
                  >
                    {cat.emoji ? (
                      <CategoryIcon value={cat.emoji} className="h-5 w-5 text-xl" />
                    ) : (
                      <Tag className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-[10.5px] sm:text-[11px] font-semibold text-center leading-tight line-clamp-2 text-foreground/80 group-hover:text-primary transition-colors">
                    {cat.name}
                  </span>
                </Link>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => scroll("next")}
            aria-label="Siguiente"
            className="hidden sm:flex absolute right-1 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm border border-white/60 dark:border-white/10 shadow-sm items-center justify-center hover:bg-white transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
